import { all, call, put, race, select, take, takeLatest } from 'redux-saga/effects';
import notifications from 'src/redux/notifications/actions';
import type { RootState } from 'src/redux/store';
import ApiInstance from 'src/helpers/apiInstance';
import { sendRequest } from 'src/helpers/saga';
import { logger } from 'src/helpers/logger';
import { t } from 'src/i18n';
import { isSuccess } from 'src/containers/admin/pricing/rateEngine/rateEngineShared/apiStatus';
import { CONTRACT_EDITOR_MODES } from '../constants';
import { requestForAction } from '../shared/actionHelpers';
import parseErrors, { extractBlockedRemovals } from '../shared/parseErrors';
import actions from './actions';
import actionTypes from './actionTypes';
import type { IEditAgencyContractState, ILoadCombinationsPayload, ISubmitRequestPayload } from './types';

const combinationsApi = new ApiInstance('agency_contracts/scope_combinations');
const contractsApi = new ApiInstance('agency_contracts');

const HTTP_UNPROCESSABLE = 422;

const logRefusal = (path: string, result: { status?: number } | undefined, detail = '') =>
  logger.error(new Error(`[agencyContractsV2] ${path} answered ${result?.status ?? 'network error'}${detail}`));

export function* loadCombinations({
  payload,
}: {
  type: string;
  payload: ILoadCombinationsPayload;
}): Generator<any, void, any> {
  try {
    const { result, cleared } = yield race({
      result: call(sendRequest, combinationsApi.post, {
        data: {
          unit_ids: payload.unitIds,
          pricing_engine_id: payload.pricingEngineId,
          ...(payload.agencyId ? { agency_id: payload.agencyId } : {}),
          ...(payload.agencyContractId ? { agency_contract_id: payload.agencyContractId } : {}),
          ...(payload.effectiveOn ? { effective_on: payload.effectiveOn } : {}),
        },
      }),
      cleared: take([actionTypes.CLEAR_COMBINATIONS, actionTypes.CLEAR_STORE]),
    });

    if (cleared) return;

    if (isSuccess(result) && Array.isArray(result.data?.records)) {
      yield put(actions.loadCombinationsSuccess(result.data));

      return;
    }

    logRefusal(combinationsApi.pathTemplate, result);
    yield put(actions.loadCombinationsError());
  } catch (error) {
    logger.error(error);

    yield put(actions.loadCombinationsError());
  }
}

function requestFor(payload: ISubmitRequestPayload) {
  if (payload.mode === CONTRACT_EDITOR_MODES.CREATE) {
    return { request: contractsApi.post, args: { data: payload.body }, path: contractsApi.pathTemplate };
  }

  const request = requestForAction(payload.endpoint);

  return request ? { request, args: { data: payload.body }, path: payload.endpoint?.url ?? '' } : null;
}

export function* submit({ payload }: { type: string; payload: ISubmitRequestPayload }): Generator<any, void, any> {
  try {
    const submitRequest = requestFor(payload);

    if (!submitRequest) {
      yield put(actions.submitFailed({ base: [t('dash.agencies.contractsV2.errors.action_unavailable')] }));

      return;
    }

    const result = yield call(sendRequest, submitRequest.request, submitRequest.args);

    // The editor that asked may have been left meanwhile (its cleanup resets `isSubmitting`). The write reached
    // the server either way, so the outcome is still toasted wherever the operator is; only the redirect is skipped.
    const stillEditing: boolean = yield select(
      (state: RootState) => (state.AgencyContractsV2Editor as IEditAgencyContractState).isSubmitting,
    );

    if (!stillEditing) {
      logger.warn(
        new Error(
          `[agencyContractsV2] ${submitRequest.path} answered ${
            result?.status ?? 'network error'
          } after the editor was left`,
        ),
      );

      if (isSuccess(result)) {
        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t(
              payload.mode === CONTRACT_EDITOR_MODES.CREATE
                ? 'dash.agencies.contractsV2.notifications.created'
                : 'dash.agencies.contractsV2.notifications.saved',
            ),
          }),
        );

        return;
      }

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: result?.messages?.[0] ?? t('dash.common.notification.something_went_wrong'),
        }),
      );

      return;
    }

    if (isSuccess(result)) {
      if (payload.mode === CONTRACT_EDITOR_MODES.CREATE) {
        // The contract exists either way — this is a 2xx. Without its body there is nothing to navigate
        // to, and reporting success invites a second submit that creates a second contract, or a second agency.
        if (!result.data?.id) {
          logRefusal(submitRequest.path, result, ' without a contract id');
          yield put(actions.submitFailed({ base: [t('dash.agencies.contractsV2.errors.created_without_body')] }));

          return;
        }

        yield put(actions.submitSuccess({ mode: CONTRACT_EDITOR_MODES.CREATE, contract: result.data, delta: null }));

        return;
      }

      yield put(actions.submitSuccess({ mode: payload.mode, contract: null, delta: result.data?.delta ?? null }));

      return;
    }

    if (result?.status === HTTP_UNPROCESSABLE) {
      const validations = result.data?.validations;
      const parsed = parseErrors(validations);

      if (!Object.keys(parsed).length) {
        logRefusal(submitRequest.path, result, ' with no field errors');
        parsed.base = result.validationMessages ??
          result.messages ?? [t('dash.common.notification.something_went_wrong')];
      }

      yield put(actions.submitFailed(parsed, extractBlockedRemovals(validations)));

      return;
    }

    logRefusal(submitRequest.path, result);
    yield put(
      actions.submitFailed({
        base: [result?.messages?.[0] ?? t('dash.common.notification.something_went_wrong')],
      }),
    );
  } catch (error) {
    logger.error(error);

    yield put(actions.submitFailed({ base: [t('dash.common.notification.something_went_wrong')] }));
  }
}

export default function* rootSaga() {
  yield all([
    takeLatest(actionTypes.LOAD_COMBINATIONS_REQUEST, loadCombinations),
    takeLatest(actionTypes.SUBMIT_REQUEST, submit),
  ]);
}
